import React from 'react';
import theme from '../theme';

const Privacy = () => {
  const styles = {
    container: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: `${theme.spacing['2xl']} ${theme.spacing.md}`,
    },
    title: {
      fontSize: theme.typography.fontSize['4xl'],
      fontWeight: theme.typography.fontWeight.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
      fontFamily: theme.typography.fontFamily.heading,
    },
    intro: {
      fontSize: theme.typography.fontSize.base,
      color: theme.colors.textSecondary,
      lineHeight: theme.typography.lineHeight.relaxed,
      marginBottom: theme.spacing.lg,
      fontFamily: theme.typography.fontFamily.primary,
    },
    section: {
      marginBottom: theme.spacing.xl,
      padding: theme.spacing.lg,
      border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.lg,
      backgroundColor: theme.colors.backgroundGray,
    },
    languageLabel: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.textSecondary,
      marginBottom: theme.spacing.md,
      fontFamily: theme.typography.fontFamily.primary,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontWeight: theme.typography.fontWeight.semibold,
    },
    sectionItem: {
      marginBottom: theme.spacing.lg,
    },
    itemTitle: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.xs,
      fontFamily: theme.typography.fontFamily.heading,
    },
    paragraph: {
      fontSize: theme.typography.fontSize.base,
      color: theme.colors.textSecondary,
      lineHeight: theme.typography.lineHeight.relaxed,
      marginBottom: 0,
      fontFamily: theme.typography.fontFamily.primary,
    },
    list: {
      marginTop: theme.spacing.sm,
      marginBottom: 0,
      paddingLeft: theme.spacing.lg,
      color: theme.colors.textSecondary,
      fontFamily: theme.typography.fontFamily.primary,
      lineHeight: theme.typography.lineHeight.relaxed,
    },
    listItem: {
      marginBottom: theme.spacing.xs,
    },
    rtl: {
      direction: 'rtl',
      textAlign: 'right',
    },
    rtlList: {
      paddingRight: theme.spacing.lg,
      paddingLeft: 0,
    },
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>WODGoat Privacy Policy</h1>
      <p style={styles.intro}>Effective Date: March 3, 2026</p>
      <p style={styles.intro}>
        WODGoat (“we”, “our”, or “us”) operates the WODGoat mobile application (the “App”). This Privacy Policy explains
        how we collect, use, disclose, and safeguard your information when you use our App.
      </p>
      <p style={styles.intro}>By using WODGoat, you agree to the collection and use of information in accordance with this policy.</p>

      <div style={styles.section}>
        <p style={styles.languageLabel}>English</p>
        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>1. Information We Collect</h3>
          <p style={styles.paragraph}>a) Personal Information</p>
          <p style={styles.paragraph}>When you create an account, we may collect:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Email address</li>
            <li style={styles.listItem}>Display name</li>
            <li style={styles.listItem}>Profile picture (if uploaded)</li>
            <li style={styles.listItem}>Authentication identifiers</li>
          </ul>
          <p style={styles.paragraph}>b) Workout &amp; Performance Data</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Logged workouts</li>
            <li style={styles.listItem}>PRs (Personal Records)</li>
            <li style={styles.listItem}>Voice workout logs (Speech-to-Text input)</li>
            <li style={styles.listItem}>Leaderboard participation data</li>
            <li style={styles.listItem}>Group activity data</li>
          </ul>
          <p style={styles.paragraph}>c) Subscription Information</p>
          <p style={styles.paragraph}>
            Subscriptions are processed securely through Apple App Store or Google Play. We use RevenueCat to manage
            subscription status. We do not store your payment card details.
          </p>
          <p style={styles.paragraph}>d) Device Information</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Device type</li>
            <li style={styles.listItem}>Operating system</li>
            <li style={styles.listItem}>Anonymous usage data</li>
            <li style={styles.listItem}>Push notification tokens</li>
          </ul>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>2. How We Use Your Information</h3>
          <p style={styles.paragraph}>We use your data to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Provide workout logging functionality</li>
            <li style={styles.listItem}>Enable leaderboards and group competitions</li>
            <li style={styles.listItem}>Process subscriptions</li>
            <li style={styles.listItem}>Send push notifications (if enabled)</li>
            <li style={styles.listItem}>Improve app performance and features</li>
            <li style={styles.listItem}>Respond to support requests</li>
          </ul>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>3. Speech-to-Text Processing</h3>
          <p style={styles.paragraph}>
            If you use voice logging, your speech may be processed by third-party speech recognition services. Voice
            data is processed only to convert speech into workout text entries.
          </p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>4. Push Notifications</h3>
          <p style={styles.paragraph}>We may send notifications related to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Workout reminders</li>
            <li style={styles.listItem}>Subscription updates</li>
            <li style={styles.listItem}>Group activity</li>
          </ul>
          <p style={styles.paragraph}>You can disable notifications in your device settings at any time.</p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>5. Data Storage &amp; Security</h3>
          <p style={styles.paragraph}>
            We use Firebase (Authentication &amp; Firestore) to securely store user data. We implement
            industry-standard security measures to protect your data. However, no method of transmission over the
            internet is 100% secure.
          </p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>6. Data Retention</h3>
          <p style={styles.paragraph}>
            We retain your information as long as your account remains active. If you delete your account, your
            personal data will be permanently deleted within a reasonable timeframe unless required by law.
          </p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>7. Account Deletion</h3>
          <p style={styles.paragraph}>You may delete your account directly inside the app. Upon deletion:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Authentication data is removed</li>
            <li style={styles.listItem}>Stored workout data is deleted</li>
            <li style={styles.listItem}>Subscription status is disconnected</li>
          </ul>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>8. International Data Transfers</h3>
          <p style={styles.paragraph}>
            Your information may be processed and stored in countries outside your residence. By using the App, you
            consent to such transfers.
          </p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>9. Children’s Privacy</h3>
          <p style={styles.paragraph}>
            WODGoat is not intended for children under 13. We do not knowingly collect data from children.
          </p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>10. Legal Basis for Processing (GDPR)</h3>
          <p style={styles.paragraph}>If you are located in the European Economic Area (EEA), we process your data based on:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Your consent</li>
            <li style={styles.listItem}>Performance of a contract</li>
            <li style={styles.listItem}>Legitimate interests</li>
          </ul>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>11. Your Rights</h3>
          <p style={styles.paragraph}>Depending on your location, you may have the right to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Access your data</li>
            <li style={styles.listItem}>Correct inaccurate data</li>
            <li style={styles.listItem}>Request deletion</li>
            <li style={styles.listItem}>Restrict processing</li>
            <li style={styles.listItem}>Data portability</li>
          </ul>
          <p style={styles.paragraph}>To exercise these rights, contact us at: wodgoat@gmail.com</p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>12. Third-Party Services</h3>
          <p style={styles.paragraph}>We may use:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Firebase (Authentication &amp; Database)</li>
            <li style={styles.listItem}>RevenueCat (Subscription Management)</li>
            <li style={styles.listItem}>Apple App Store / Google Play (Payments)</li>
            <li style={styles.listItem}>Speech-to-Text providers</li>
          </ul>
          <p style={styles.paragraph}>Each third-party service has its own privacy policy.</p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>13. Health &amp; Fitness Disclaimer</h3>
          <p style={styles.paragraph}>
            WODGoat provides fitness tracking tools only. We do not provide medical advice. Consult a qualified
            healthcare professional before beginning any exercise program.
          </p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>14. Changes to This Policy</h3>
          <p style={styles.paragraph}>
            We may update this Privacy Policy from time to time. Changes will be reflected with an updated Effective
            Date.
          </p>
        </div>

        <div style={styles.sectionItem}>
          <h3 style={styles.itemTitle}>15. Contact Us</h3>
          <p style={styles.paragraph}>If you have questions about this Privacy Policy, contact: wodgoat@gmail.com</p>
        </div>
      </div>

      <div style={styles.section}>
        <p style={styles.languageLabel}>العربية</p>
        <div style={styles.rtl}>
          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>سياسة الخصوصية لتطبيق WODGoat</h3>
            <p style={styles.paragraph}>تاريخ السريان: March 3, 2026</p>
            <p style={styles.paragraph}>يتم تشغيل تطبيق WODGoat ("التطبيق") بواسطة يحيى محمد أنس.</p>
            <p style={styles.paragraph}>توضح سياسة الخصوصية هذه كيفية جمع واستخدام وحماية بياناتك عند استخدام التطبيق.</p>
            <p style={styles.paragraph}>باستخدامك للتطبيق، فإنك توافق على جمع واستخدام المعلومات وفقًا لهذه السياسة.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>1. المعلومات التي نجمعها</h3>
            <p style={styles.paragraph}>أ) المعلومات الشخصية</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>البريد الإلكتروني</li>
              <li style={styles.listItem}>اسم العرض</li>
              <li style={styles.listItem}>الصورة الشخصية (إن تم رفعها)</li>
              <li style={styles.listItem}>بيانات تسجيل الدخول</li>
            </ul>
            <p style={styles.paragraph}>ب) بيانات التمارين والأداء</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>سجل التمارين</li>
              <li style={styles.listItem}>أرقام PR الشخصية</li>
              <li style={styles.listItem}>تسجيل الصوت للتحويل إلى نص</li>
              <li style={styles.listItem}>بيانات المشاركة في المجموعات واللوائح</li>
            </ul>
            <p style={styles.paragraph}>ج) معلومات الاشتراكات</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>تتم إدارة الاشتراكات عبر Apple App Store أو Google Play</li>
              <li style={styles.listItem}>يتم إدارة حالة الاشتراك عبر RevenueCat</li>
              <li style={styles.listItem}>لا نقوم بتخزين بيانات البطاقة الائتمانية</li>
            </ul>
            <p style={styles.paragraph}>د) معلومات الجهاز</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>نوع الجهاز ونظام التشغيل</li>
              <li style={styles.listItem}>بيانات الاستخدام المجهولة</li>
              <li style={styles.listItem}>رموز إشعارات الدفع</li>
            </ul>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>2. كيفية استخدام المعلومات</h3>
            <p style={styles.paragraph}>نستخدم بياناتك لـ:</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>توفير تسجيل التمارين</li>
              <li style={styles.listItem}>تمكين اللوائح والمجموعات</li>
              <li style={styles.listItem}>معالجة الاشتراكات</li>
              <li style={styles.listItem}>إرسال الإشعارات</li>
              <li style={styles.listItem}>تحسين أداء التطبيق</li>
              <li style={styles.listItem}>الرد على الاستفسارات والدعم</li>
            </ul>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>3. تحويل الصوت إلى نص</h3>
            <p style={styles.paragraph}>يتم معالجة تسجيلات الصوت بواسطة خدمات طرف ثالث لتحويل الكلام إلى نص.</p>
            <p style={styles.paragraph}>تُستخدم البيانات فقط لإنشاء سجلات التمارين.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>4. إشعارات الدفع</h3>
            <p style={styles.paragraph}>نرسل إشعارات حول:</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>تذكيرات التمرين</li>
              <li style={styles.listItem}>تحديثات الاشتراك</li>
              <li style={styles.listItem}>نشاط المجموعات</li>
            </ul>
            <p style={styles.paragraph}>يمكنك تعطيل الإشعارات من إعدادات الجهاز في أي وقت.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>5. تخزين البيانات والأمان</h3>
            <p style={styles.paragraph}>نستخدم Firebase (المصادقة و Firestore) لتخزين البيانات بأمان.</p>
            <p style={styles.paragraph}>نطبق تدابير أمان قياسية لحماية البيانات.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>6. الاحتفاظ بالبيانات</h3>
            <p style={styles.paragraph}>يتم الاحتفاظ بالبيانات طالما الحساب نشط.</p>
            <p style={styles.paragraph}>عند حذف الحساب، يتم حذف البيانات ضمن إطار زمني معقول.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>7. حذف الحساب</h3>
            <p style={styles.paragraph}>يمكنك حذف الحساب مباشرة من التطبيق. بعد الحذف:</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>يتم إزالة بيانات المصادقة</li>
              <li style={styles.listItem}>حذف بيانات التمارين</li>
              <li style={styles.listItem}>فصل حالة الاشتراك</li>
            </ul>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>8. نقل البيانات دوليًا</h3>
            <p style={styles.paragraph}>قد تتم معالجة بياناتك في دول خارج مكان إقامتك.</p>
            <p style={styles.paragraph}>باستخدامك التطبيق، فإنك توافق على ذلك.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>9. خصوصية الأطفال</h3>
            <p style={styles.paragraph}>التطبيق غير مخصص لمن هم أقل من 13 عامًا.</p>
            <p style={styles.paragraph}>لا نقوم بجمع بيانات الأطفال عمدًا.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>10. حقوق المستخدم</h3>
            <p style={styles.paragraph}>قد يكون لك الحق في:</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>الوصول إلى البيانات</li>
              <li style={styles.listItem}>تصحيح البيانات</li>
              <li style={styles.listItem}>طلب الحذف</li>
              <li style={styles.listItem}>تقييد المعالجة</li>
              <li style={styles.listItem}>نقل البيانات</li>
            </ul>
            <p style={styles.paragraph}>لممارسة هذه الحقوق، تواصل معنا على: wodgoat@gmail.com</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>11. خدمات الطرف الثالث</h3>
            <p style={styles.paragraph}>قد نستخدم:</p>
            <ul style={{ ...styles.list, ...styles.rtlList }}>
              <li style={styles.listItem}>Firebase (المصادقة و قاعدة البيانات)</li>
              <li style={styles.listItem}>RevenueCat (إدارة الاشتراكات)</li>
              <li style={styles.listItem}>Apple App Store / Google Play (المدفوعات)</li>
              <li style={styles.listItem}>خدمات تحويل الصوت إلى نص</li>
            </ul>
            <p style={styles.paragraph}>لكل خدمة طرف ثالث سياسة خصوصية خاصة بها.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>12. إخلاء المسؤولية الصحية</h3>
            <p style={styles.paragraph}>التطبيق لأغراض تتبع اللياقة فقط.</p>
            <p style={styles.paragraph}>لا نقدم نصائح طبية.</p>
            <p style={styles.paragraph}>استشر مختص صحي قبل بدء أي برنامج تمرين.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>13. التغييرات على السياسة</h3>
            <p style={styles.paragraph}>قد يتم تحديث سياسة الخصوصية من وقت لآخر.</p>
            <p style={styles.paragraph}>سيتم عرض تاريخ السريان الجديد مع التغييرات.</p>
          </div>

          <div style={styles.sectionItem}>
            <h3 style={styles.itemTitle}>14. التواصل</h3>
            <p style={styles.paragraph}>للاستفسارات: wodgoat@gmail.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
